import { Button, Icon, Typography } from "@platform/design-system";
import { Box, CircularProgress, Stack } from "@mui/material";

import { timelineOf, type TimelineEntry } from "../../messageSummary";

import { EventRow } from "./EventRow";
import { MessageCard } from "./MessageCard";

import type {
  CorrespondenceMessage,
  CorrespondenceThreadEvent,
} from "@platform/api";

export interface ThreadViewProps {
  messages: CorrespondenceMessage[];
  events: CorrespondenceThreadEvent[];
  /**
   * The signed-in analyst's address, so their own messages read as `You`.
   *
   * Optional: a surface that does not know who is reading shows every
   * message under its sender's name, which is true, just less personal.
   */
  viewerAddress?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onCollapse: () => void;
  /**
   * Toggles one message's read state. Absent hides the per-message controls
   * rather than showing ones that are refused.
   */
  onSetRead?: (messageId: string, read: boolean) => void;
  /** Marks the whole thread read, which is the footer control. */
  onMarkThreadRead?: () => void;
  /** True while a mark is in flight, so the footer control cannot double-fire. */
  isMarking?: boolean;
}

/**
 * The conversation, under the row it belongs to.
 *
 * Newest first. The service answers in the order the exchange was lived,
 * which is right for a transcript and wrong for a drawer: an analyst opens a
 * thread to see what just arrived, and a long exchange would otherwise open
 * on its first message.
 *
 * The design's footer also carries Reply and Mark thread read. Neither is
 * here: `SendReply` answers Unimplemented until the secure-document path
 * lands (CD-2989), and nothing in the service records who has read what yet.
 * Both arrive with the thing that backs them.
 */
export const ThreadView = ({
  messages,
  events,
  viewerAddress,
  isLoading = false,
  isError = false,
  onRetry,
  onCollapse,
  onSetRead,
  onMarkThreadRead,
  isMarking = false,
}: ThreadViewProps): React.JSX.Element => {
  if (isLoading) {
    return (
      <Centered testId="thread-view-loading">
        <CircularProgress aria-label="Loading the conversation" />
      </Centered>
    );
  }

  if (isError) {
    return (
      <Centered testId="thread-view-error">
        <Typography variant="body-medium">
          This conversation could not be loaded.
        </Typography>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Centered>
    );
  }

  const timeline = timelineOf(messages, events, viewerAddress);

  return (
    <Stack
      data-testid="thread-view"
      spacing={1.5}
      sx={{ px: 2, py: 2, backgroundColor: "action.hover" }}
    >
      {timeline.length === 0 ? (
        <Centered testId="thread-view-empty">
          <Typography variant="body-medium" color="textSecondary">
            There is nothing on this thread yet.
          </Typography>
        </Centered>
      ) : (
        timeline.map((entry) =>
          entry.kind === "message" ? (
            <MessageCard key={entry.id} message={entry} onSetRead={onSetRead} />
          ) : (
            <EventRow key={entry.id} event={entry} />
          ),
        )
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="tertiary"
          onClick={onCollapse}
          startIcon={<Icon name="collapse" size="sm" />}
        >
          Collapse
        </Button>

        {/*
          Reply belongs here in the design and is not offered: SendReply
          answers Unimplemented until the secure-document path lands
          (CD-2989), and a control that always refuses is worse than none.
        */}
        {onMarkThreadRead && (
          <Button
            data-testid="thread-view-mark-read"
            variant="secondary"
            disabled={isMarking || allRead(timeline)}
            onClick={onMarkThreadRead}
          >
            Mark thread read
          </Button>
        )}
      </Box>
    </Stack>
  );
};

/**
 * Whether every message on the thread has been read.
 *
 * The footer control is disabled on it, so a thread with nothing waiting
 * offers no write that would change nothing.
 */
const allRead = (timeline: TimelineEntry[]): boolean =>
  !timeline.some((entry) => entry.kind === "message" && !entry.read);

const Centered = ({
  testId,
  children,
}: {
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <Stack
    data-testid={testId}
    spacing={1}
    alignItems="center"
    justifyContent="center"
    sx={{ py: 4 }}
  >
    {children}
  </Stack>
);
